import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Button,
  Typography,
  Paper,
  Chip,
  Divider,
  IconButton,
  Tab,
  Tabs,
  Alert,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Breadcrumbs,
  Link,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import {
  ArrowBack,
  PlayArrow,
  Refresh,
  Delete,
  Add,
  OpenInNew,
  ContentCopy,
  GitHub,
  Cloud,
  Dns,
  Terminal,
  Circle,
  Stop,
  RestartAlt,
  Edit,
  Lock,
  Language,
  Menu as MenuIcon,
  Save,
  AddCircleOutline,
  RemoveCircleOutline,
} from "@mui/icons-material";
import {
  fetchApplicationById,
  updateApplication,
  deleteApplication,
  deployApplication,
  redeployApplication,
  deployService,
  startService,
  stopService,
  restartService,
  deleteService,
  updateService,
  fetchServiceLogs,
  clearCurrentApplication,
  updateServiceStatus,
} from "../../redux/slices/applicationSlice";
import ConfirmDialog from "../../components/ConfirmDialog";
import AddServiceDialog from "./AddServiceDialog";
import AppCustomDomainSection from "./AppCustomDomainSection";
import ApmTab from "../../components/ApmTab";
import {
  useVerificationGuard,
  VERIFY_MSG,
} from "../../hooks/useVerificationGuard";
import { useSnackbar } from "../../components/common/CustomSnackbar";
import { useApplicationSocket } from "../../hooks/useApplicationSocket";

const statusStyles = {
  PENDING: { color: "#f59e0b", bg: "#fef3c7", label: "Pending" },
  ACTIVE: { color: "#10b981", bg: "#d1fae5", label: "Active" },
  UPDATING: { color: "#3b82f6", bg: "#dbeafe", label: "Updating" },
  SUSPENDED: { color: "#ef4444", bg: "#fee2e2", label: "Suspended" },
  MAINTENANCE: { color: "#6366f1", bg: "#e0e7ff", label: "Maintenance" },
};

const serviceStatusStyles = {
  PENDING: { color: "#f59e0b", label: "Pending" },
  PULLING: { color: "#3b82f6", label: "Pulling" },
  CREATING: { color: "#3b82f6", label: "Creating" },
  STARTING: { color: "#3b82f6", label: "Starting" },
  RUNNING: { color: "#10b981", label: "Running" },
  STOPPING: { color: "#f59e0b", label: "Stopping" },
  STOPPED: { color: "#6b7280", label: "Stopped" },
  FAILED: { color: "#ef4444", label: "Failed" },
  UNHEALTHY: { color: "#ef4444", label: "Unhealthy" },
};

const typeIcons = {
  WORDPRESS: <Dns sx={{ fontSize: 20 }} />,
  DOCKER_IMAGE: <Cloud sx={{ fontSize: 20 }} />,
  DOCKER_COMPOSE: <Cloud sx={{ fontSize: 20 }} />,
  GIT_DOCKERFILE: <GitHub sx={{ fontSize: 20 }} />,
};

const typeLabels = {
  WORDPRESS: "WordPress",
  DOCKER_IMAGE: "Docker Image",
  DOCKER_COMPOSE: "Docker Compose",
  GIT_DOCKERFILE: "Git + Dockerfile",
};

const ApplicationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isVerifiedOrAdmin = useVerificationGuard();

  const {
    currentApplication: app,
    loading,
    error,
    deployLoading,
    serviceLogs,
  } = useSelector((state) => state.applications);

  const [activeTab, setActiveTab] = useState(0);
  const [addServiceOpen, setAddServiceOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", description: "" });
  // const [logsOpen, setLogsOpen] = useState(false);
  // const [logsServiceId, setLogsServiceId] = useState(null);
  const [actionMenu, setActionMenu] = useState(null);
  const [logsActiveService, setLogsActiveService] = useState(null); // id of active tab
  const [logsAutoRefresh, setLogsAutoRefresh] = useState(false);
  // const [logsRefreshInterval, setLogsRefreshInterval] = useState(null);
  const [volumeEditing, setVolumeEditing] = useState(null); // serviceId
  const [volumeRows, setVolumeRows] = useState([]);

  // Custom snackbar hook (top-right position)
  const { notify } = useSnackbar();
  const showSnackbar = (message, severity = "success") => {
    if (severity === "error") notify.error(message);
    else if (severity === "warning") notify.warning(message);
    else if (severity === "info") notify.info(message);
    else notify.success(message);
  };

  // Real-time socket updates for this application
  const { deployLogs, clearDeployLogs } = useApplicationSocket(id, {
    onStatusChange: (data) => {
      notify.info(data.message || `Application status: ${data.status}`);
    },
    onServiceStatus: (data) => {
      if (data.status === "RUNNING") {
        notify.success(data.message || "Service is running");
      } else if (data.status === "FAILED") {
        notify.error(data.message || "Service operation failed");
      } else if (data.status === "STOPPED") {
        notify.info(data.message || "Service stopped");
      }
    },
  });

  // Confirmation dialogs
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
    confirmColor: "primary",
    confirmText: "Confirm",
  });
  const openConfirm = (opts) => setConfirmDialog({ open: true, ...opts });
  const closeConfirm = () => setConfirmDialog((d) => ({ ...d, open: false }));

  // Env var editor
  const [envEditing, setEnvEditing] = useState(null); // serviceId being edited
  const [envRows, setEnvRows] = useState([]);
  const [envSaving, setEnvSaving] = useState(false);
  const [envDirty, setEnvDirty] = useState(false);

  // Git/Build config editor
  const [gitConfigEditing, setGitConfigEditing] = useState(null); // serviceId
  const [gitConfigForm, setGitConfigForm] = useState({});
  const [gitConfigSaving, setGitConfigSaving] = useState(false);

  // Container logs options
  const [logsTail, setLogsTail] = useState(100);
  const [logsMode, setLogsMode] = useState("container"); // "container" or "deployment"

  const protocol =
    process.env.REACT_APP_SSL_ENABLED === "true" ? "https" : "http";

  const logsActiveServiceRef = useRef(null);
  const refreshIntervalRef = useRef(null);
  const isSensitiveKey = (key) =>
    /password|secret|token|key|credential/i.test(key);

  useEffect(() => {
    dispatch(fetchApplicationById(id));
    return () => dispatch(clearCurrentApplication());
  }, [dispatch, id]);

  useEffect(() => {
    if (app)
      setEditForm({ name: app.name, description: app.description || "" });
  }, [app]);

  const refresh = useCallback(
    () => dispatch(fetchApplicationById(id)),
    [dispatch, id],
  );

  const copy = (text) => navigator.clipboard.writeText(text);

  // Actions
  const handleDeployAll = async () => {
    if (!isVerifiedOrAdmin)
      return openConfirm({
        title: "Verification Required",
        message: VERIFY_MSG,
        confirmText: "OK",
        onConfirm: closeConfirm,
      });
    clearDeployLogs();
    notify.info("Deploying all services...");
    try {
      await dispatch(deployApplication(id)).unwrap();
      notify.success("Deployment initiated for all services");
    } catch (err) {
      notify.error(err?.error || err?.message || "Deployment failed");
    }
    refresh();
  };

  const handleRedeploy = async () => {
    if (!isVerifiedOrAdmin)
      return openConfirm({
        title: "Verification Required",
        message: VERIFY_MSG,
        confirmText: "OK",
        onConfirm: closeConfirm,
      });
    openConfirm({
      title: "Redeploy Application",
      message:
        "This will stop and recreate all containers with the latest configuration. Continue?",
      confirmText: "Redeploy",
      confirmColor: "primary",
      onConfirm: async () => {
        closeConfirm();
        clearDeployLogs();
        notify.info("Redeploying all services...");
        try {
          await dispatch(redeployApplication(id)).unwrap();
          notify.success("Redeployment initiated for all services");
        } catch (err) {
          notify.error(err?.error || err?.message || "Redeployment failed");
        }
        refresh();
      },
    });
  };

  const handleDelete = async () => {
    if (!isVerifiedOrAdmin)
      return openConfirm({
        title: "Verification Required",
        message: VERIFY_MSG,
        confirmText: "OK",
        onConfirm: closeConfirm,
      });
    openConfirm({
      title: "Delete Application",
      message: `Are you sure you want to delete "${app.name}" and all its services? This action will stop all running containers.`,
      confirmText: "Delete",
      confirmColor: "error",
      onConfirm: async () => {
        closeConfirm();
        await dispatch(deleteApplication(id));
        navigate("/dashboard/applications");
      },
    });
  };

  const handleEditSave = async () => {
    if (!isVerifiedOrAdmin)
      return openConfirm({
        title: "Verification Required",
        message: VERIFY_MSG,
        confirmText: "OK",
        onConfirm: closeConfirm,
      });
    await dispatch(updateApplication({ appId: id, data: editForm }));
    setEditOpen(false);
  };

  // Git/Build config handlers
  const handleGitConfigEdit = (service) => {
    const bc = service.buildConfig || {};
    setGitConfigEditing(service.id);
    setGitConfigForm({
      repoUrl: bc.repoUrl || "",
      branch: bc.branch || "main",
      dockerfilePath: bc.dockerfilePath || "Dockerfile",
      buildContext: bc.buildContext || ".",
      accessToken: bc.credentials?.token || "",
    });
  };

  const handleGitConfigSave = async () => {
    if (!gitConfigEditing) return;
    setGitConfigSaving(true);
    try {
      const service = app.services.find((s) => s.id === gitConfigEditing);
      const existingConfig = service?.buildConfig || {};
      const buildConfig = {
        ...existingConfig,
        repoUrl: gitConfigForm.repoUrl,
        branch: gitConfigForm.branch,
        dockerfilePath: gitConfigForm.dockerfilePath,
        buildContext: gitConfigForm.buildContext,
        ...(gitConfigForm.accessToken
          ? { credentials: { type: "token", token: gitConfigForm.accessToken } }
          : { credentials: null }),
      };
      await dispatch(
        updateService({
          appId: id,
          serviceId: gitConfigEditing,
          data: { buildConfig },
        }),
      );
      showSnackbar("Build configuration updated");
      setGitConfigEditing(null);
      refresh();
    } catch (e) {
      showSnackbar(e.message || "Failed to update build config", "error");
    } finally {
      setGitConfigSaving(false);
    }
  };

  // Service actions
  const svcDeploy = async (sid) => {
    try {
      await dispatch(deployService({ appId: id, serviceId: sid }));
      refresh();
      const svcName = services.find((s) => s.id === sid)?.name || "Service";
      showSnackbar(`${svcName} deployment initiated.`);
    } catch (err) {
      showSnackbar(
        `Deployment failed: ${err.message || "Unknown error"}`,
        "error",
      );
    }
  };

  const svcStart = async (sid) => {
    // Optimistic update – mark as STARTING immediately
    dispatch(
      updateServiceStatus({ appId: id, serviceId: sid, status: "STARTING" }),
    );

    try {
      await dispatch(startService({ appId: id, serviceId: sid }));
      // After successful start, update to RUNNING (optimistic)
      dispatch(
        updateServiceStatus({ appId: id, serviceId: sid, status: "RUNNING" }),
      );
    } catch (err) {
      // Revert on failure – set back to STOPPED or whatever the previous status was
      dispatch(
        updateServiceStatus({ appId: id, serviceId: sid, status: "STOPPED" }),
      );
      showSnackbar("Failed to start service", "error");
      return;
    }

    // Refresh to get actual full data from server (covers all fields)
    refresh();
    const svcName = services.find((s) => s.id === sid)?.name || "Service";
    showSnackbar(`${svcName} started successfully.`);
  };
  const svcStop = async (sid) => {
    // Optimistic update BEFORE the API call
    dispatch(
      updateServiceStatus({ appId: id, serviceId: sid, status: "STOPPED" }),
    );

    try {
      await dispatch(stopService({ appId: id, serviceId: sid }));
      refresh();
    } catch (error) {
      // Revert on failure
      dispatch(
        updateServiceStatus({ appId: id, serviceId: sid, status: "RUNNING" }),
      );
      showSnackbar("Failed to stop service", "error");
      return;
    }

    const svcName = services.find((s) => s.id === sid)?.name || "Service";
    showSnackbar(`${svcName} stopped successfully.`, "info");
  };

  const svcRestart = async (sid) => {
    // Optimistic update (optional, but nice)
    dispatch(
      updateServiceStatus({ appId: id, serviceId: sid, status: "STARTING" }),
    );
    try {
      await dispatch(restartService({ appId: id, serviceId: sid }));
      dispatch(
        updateServiceStatus({ appId: id, serviceId: sid, status: "RUNNING" }),
      );
      refresh();
      const svcName = services.find((s) => s.id === sid)?.name || "Service";
      showSnackbar(`${svcName} restarted successfully.`);
    } catch (err) {
      // Revert to previous status (we don't know it exactly, but likely RUNNING or STOPPED)
      // Simple fallback: just refresh to get real state
      refresh();
      showSnackbar("Failed to restart service", "error");
    }
  };

  const svcDelete = async (sid) => {
    if (!isVerifiedOrAdmin)
      return openConfirm({
        title: "Verification Required",
        message: VERIFY_MSG,
        confirmText: "OK",
        onConfirm: closeConfirm,
      });
    const svcName = services.find((s) => s.id === sid)?.name || "this service";
    openConfirm({
      title: "Delete Service",
      message: `Are you sure you want to delete "${svcName}"? This will remove the container.`,
      confirmText: "Delete",
      confirmColor: "error",
      onConfirm: async () => {
        closeConfirm();
        try {
          await dispatch(deleteService({ appId: id, serviceId: sid }));
          refresh();
          showSnackbar(`${svcName} deleted successfully.`);
        } catch (err) {
          showSnackbar(
            `Failed to delete ${svcName}: ${err.message || "Unknown error"}`,
            "error",
          );
        }
      },
    });
  };

  const fetchLogs = async (serviceId) => {
    try {
      await dispatch(
        fetchServiceLogs({
          appId: id,
          serviceId,
          options: { tail: logsTail },
        }),
      ).unwrap();
    } catch (err) {
      showSnackbar("Failed to fetch logs", "error");
    }
  };

  const svcLogs = async (sid) => {
    setActiveTab(2);
    setLogsActiveService(sid);
    await fetchLogs(sid);
  };

  // Keep the ref in sync whenever state changes
  useEffect(() => {
    logsActiveServiceRef.current = logsActiveService;
  }, [logsActiveService]);

  // Start auto‑refresh
  // const startAutoRefresh = () => {
  //   stopAutoRefresh(); // clear any existing
  //   const interval = setInterval(() => {
  //     if (logsActiveServiceRef.current) {
  //       fetchLogs(logsActiveServiceRef.current);
  //     }
  //   }, 3000); // poll every 3 seconds
  //   setLogsRefreshInterval(interval);
  // };

  const startAutoRefresh = () => {
    if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
    refreshIntervalRef.current = setInterval(() => {
      if (logsActiveServiceRef.current) {
        fetchLogs(logsActiveServiceRef.current);
      }
    }, 3000);
    // setLogsAutoRefresh(true);
  };

  // const stopAutoRefresh = () => {
  //   if (logsRefreshInterval) {
  //     clearInterval(logsRefreshInterval);
  //     setLogsRefreshInterval(null);
  //   }
  // };
  const stopAutoRefresh = () => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
    // setLogsAutoRefresh(false);
  };

  // Toggle auto‑refresh
  const toggleAutoRefresh = () => {
    if (logsAutoRefresh) {
      stopAutoRefresh();
      setLogsAutoRefresh(false);
    } else {
      startAutoRefresh();
      setLogsAutoRefresh(true);
    }
    setLogsAutoRefresh(!logsAutoRefresh);
  };

  // Cleanup on unmount
  // useEffect(() => {
  //   return () => stopAutoRefresh();
  // }, []);

  useEffect(() => {
    return () => {
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
    };
  }, []);

  const formatLogLine = (line) => {
    // Docker logs line format: "2026-04-29T12:57:20.867349038Z actual message"
    const match = line.match(/^(\S+)Z?\s/);
    if (match) {
      const ts = new Date(match[1]);
      const formatted = ts.toLocaleTimeString(undefined, { hour12: false });
      const rest = line.slice(match[0].length);
      return `[${formatted}] ${rest}`;
    }
    return line;
  };

  // Auto‑select first service when entering Logs tab
  // useEffect(() => {
  //   if (activeTab === 2 && services.length > 0 && !logsActiveService) {
  //     const firstSvc = services[0];
  //     setLogsActiveService(firstSvc.id);
  //     fetchLogs(firstSvc.id);
  //   }
  // }, [activeTab, services]);

  // Env var helpers
  const startEnvEdit = (svc) => {
    const raw = svc.envVars;
    let rows;
    if (Array.isArray(raw) && raw.length > 0) {
      // New ordered array format [{key, value}]
      rows = raw.map((v) => ({ key: v.key, value: v.value }));
    } else if (raw && typeof raw === "object" && !Array.isArray(raw)) {
      // Legacy object format {KEY: value}
      const entries = Object.entries(raw);
      rows =
        entries.length > 0
          ? entries.map(([k, v]) => ({ key: k, value: v }))
          : [{ key: "", value: "" }];
    } else {
      rows = [{ key: "", value: "" }];
    }
    setEnvRows(rows);
    setEnvEditing(svc.id);
    setEnvDirty(false);
  };
  const cancelEnvEdit = () => {
    setEnvEditing(null);
    setEnvRows([]);
    setEnvDirty(false);
  };
  const addEnvRow = () => {
    setEnvRows((r) => [...r, { key: "", value: "" }]);
    setEnvDirty(true);
  };
  const removeEnvRow = (idx) => {
    setEnvRows((r) => r.filter((_, i) => i !== idx));
    setEnvDirty(true);
  };
  const updateEnvRow = (idx, field, val) => {
    setEnvRows((r) =>
      r.map((row, i) => (i === idx ? { ...row, [field]: val } : row)),
    );
    setEnvDirty(true);
  };
  const saveEnvVars = async () => {
    if (!envEditing) return;
    setEnvSaving(true);
    // Send as ordered array to preserve user's key order
    const vars = envRows
      .filter((r) => r.key.trim())
      .map((r) => ({ key: r.key.trim(), value: r.value }));
    await dispatch(
      updateService({
        appId: id,
        serviceId: envEditing,
        data: { envVars: vars },
      }),
    );
    setEnvSaving(false);
    setEnvEditing(null);
    setEnvDirty(false);
    refresh();
  };

  // Volume edit:
  const startVolumeEdit = (service) => {
    const existing = service.volumes || [];
    const rows = existing.map((v) => ({
      source: v.source || v.host || "",
      target: v.target || v.container || "",
      mode: v.mode || "rw",
      persistent: v.persistent || false,
    }));
    if (rows.length === 0)
      rows.push({ source: "", target: "", mode: "rw", persistent: false });
    setVolumeRows(rows);
    setVolumeEditing(service.id);
  };

  const saveVolumes = async (serviceId) => {
    const validRows = volumeRows.filter(
      (r) => r.source.trim() && r.target.trim(),
    );
    try {
      await dispatch(
        updateService({
          appId: id,
          serviceId,
          data: { volumes: validRows },
        }),
      );
      showSnackbar("Volumes saved — redeploy the service to apply changes.");
    } catch (e) {
      showSnackbar(e.message || "Failed to save volumes", "error");
      return; // don't close the editor on failure
    }
    refresh();
    setVolumeEditing(null);
  };

  // Loading / Error states
  if (loading && !app) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !app) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/dashboard/applications")}
        >
          Back
        </Button>
      </Box>
    );
  }

  if (!app) return null;

  const services = app.services || [];
  const domain = app.customDomain || app.generatedDomain;
  const status = statusStyles[app.status] || statusStyles.PENDING;
  const gitConfig = app.gitConfig || app.metadata?.gitConfig;

  const renderLogsContent = (serviceId) => {
    const rawLogs = serviceLogs[serviceId];
    if (!rawLogs)
      return <Typography color="text.secondary">No logs available</Typography>;
    if (typeof rawLogs !== "string")
      return (
        <Typography color="text.secondary">Invalid log data format.</Typography>
      );

    const lines = rawLogs.split("\n").filter((line) => line.trim() !== "");
    return (
      <Box
        sx={{
          bgcolor: "#0d1117",
          color: "#c9d1d9",
          p: 2,
          borderRadius: 2,
          fontFamily: "'Fira Code', 'Cascadia Code', monospace",
          fontSize: "0.8rem",
          lineHeight: 1.6,
          maxHeight: 500,
          overflow: "auto",
          whiteSpace: "pre-wrap",
          wordBreak: "break-all",
        }}
      >
        {lines.map((line, idx) => (
          <div key={idx}>{formatLogLine(line)}</div>
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: "auto" }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => navigate("/dashboard/applications")}
          underline="hover"
          color="inherit"
        >
          Applications
        </Link>
        <Typography variant="body2" color="text.primary" fontWeight={600}>
          {app.name}
        </Typography>
      </Breadcrumbs>

      {/* Header Card */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 1.5,
                bgcolor: "action.selected",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {typeIcons[app.deploymentType]}
            </Box>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h5" fontWeight={700}>
                  {app.name}
                </Typography>
                <IconButton size="small" onClick={() => setEditOpen(true)}>
                  <Edit sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.25 }}
              >
                <Chip
                  size="small"
                  label={typeLabels[app.deploymentType]}
                  sx={{
                    height: 20,
                    fontSize: "0.7rem",
                    bgcolor: "action.selected",
                  }}
                />
                <Chip
                  size="small"
                  icon={
                    <Circle
                      sx={{
                        fontSize: "8px !important",
                        color: `${status.color} !important`,
                      }}
                    />
                  }
                  label={status.label}
                  sx={{
                    height: 22,
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    bgcolor: status.bg,
                    color: status.color,
                    "& .MuiChip-icon": { ml: 0.5 },
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* Actions */}
          <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={refresh} disabled={loading}>
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              size="small"
              startIcon={<PlayArrow />}
              onClick={handleDeployAll}
              disabled={deployLoading}
              disableElevation
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5 }}
            >
              {deployLoading ? "Deploying..." : "Deploy"}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RestartAlt />}
              onClick={handleRedeploy}
              disabled={deployLoading || services.length === 0}
              sx={{ textTransform: "none", borderRadius: 1.5 }}
            >
              Redeploy
            </Button>
          </Box>
        </Box>

        {/* Access URL */}
        {domain && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 1.5,
              borderRadius: 2,
              bgcolor: "action.hover",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            {protocol === "https" ? (
              <Lock sx={{ fontSize: 16, color: "success.main" }} />
            ) : (
              <Language sx={{ fontSize: 16, color: "text.disabled" }} />
            )}
            <Typography
              variant="body2"
              sx={{
                fontFamily: "monospace",
                fontWeight: 600,
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {`${protocol}://${domain}`}
            </Typography>
            <Tooltip title="Copy URL">
              <IconButton
                size="small"
                onClick={() => copy(`${protocol}://${domain}`)}
              >
                <ContentCopy sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              variant="outlined"
              startIcon={<OpenInNew sx={{ fontSize: 14 }} />}
              onClick={() => window.open(`${protocol}://${domain}`, "_blank")}
              sx={{ textTransform: "none", borderRadius: 1.5, flexShrink: 0 }}
            >
              Visit
            </Button>
          </Box>
        )}

        {/* Description */}
        {app.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {app.description}
          </Typography>
        )}
      </Paper>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{
          mb: 3,
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 600,
            minHeight: 40,
          },
          "& .MuiTabs-indicator": { height: 2 },
        }}
      >
        <Tab label="Services" />
        <Tab label="Environment" />
        <Tab label="Logs" />
        <Tab label="APM" />
        <Tab label="Settings" />
      </Tabs>

      {/* Deployment Activity Banner */}
      {deployLoading && (
        <Alert
          severity="info"
          icon={<CircularProgress size={18} />}
          sx={{ mb: 2 }}
        >
          Deployment in progress — service statuses will update automatically...
        </Alert>
      )}

      {/* Deployment Activity Log */}
      {deployLogs.length > 0 && (
        <Paper
          variant="outlined"
          sx={{
            mb: 2,
            maxHeight: 200,
            overflow: "auto",
            p: 1.5,
            bgcolor: "grey.900",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Deployment Activity
            </Typography>
            {!deployLoading && (
              <Button size="small" onClick={clearDeployLogs}>
                Clear
              </Button>
            )}
          </Box>
          {deployLogs.map((log, idx) => (
            <Box key={idx} sx={{ display: "flex", gap: 1, py: 0.25 }}>
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}
              >
                {new Date(log.timestamp).toLocaleTimeString()}
              </Typography>
              <Chip
                label={log.step}
                size="small"
                color={
                  log.step === "failed"
                    ? "error"
                    : log.step === "running"
                      ? "success"
                      : "default"
                }
                sx={{ height: 18, fontSize: "0.65rem" }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontFamily: "monospace" }}
              >
                {log.serviceName && `[${log.serviceName}] `}
                {log.message}
              </Typography>
            </Box>
          ))}
        </Paper>
      )}

      {/* Tab: Services */}
      {activeTab === 0 && (
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 2,
              alignItems: "center",
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              {services.length} service{services.length !== 1 ? "s" : ""}
            </Typography>
            <Button
              size="small"
              startIcon={<Add />}
              onClick={() => setAddServiceOpen(true)}
              sx={{ textTransform: "none" }}
            >
              Add Service
            </Button>
          </Box>

          {services.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{
                textAlign: "center",
                py: 6,
                borderRadius: 2,
                borderStyle: "dashed",
              }}
            >
              <Cloud sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                No services
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add a service to deploy your application
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<Add />}
                onClick={() => setAddServiceOpen(true)}
                disableElevation
                sx={{ textTransform: "none", borderRadius: 1.5 }}
              >
                Add Service
              </Button>
            </Paper>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {services.map((svc) => {
                const ss =
                  serviceStatusStyles[svc.status] ||
                  serviceStatusStyles.PENDING;

                const isRunning = svc.status === "RUNNING";
                const isDeploying = [
                  "PULLING",
                  "CREATING",
                  "STARTING",
                ].includes(svc.status);
                const isStopped =
                  svc.status === "STOPPED" ||
                  svc.status === "FAILED" ||
                  svc.status === "PENDING";

                return (
                  <Paper
                    key={svc.id}
                    variant="outlined"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      p: 2,
                      borderRadius: 2,
                      borderLeft: 3,
                      borderLeftColor: ss.color,
                    }}
                  >
                    {/* Info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.25,
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight={600}>
                          {svc.name}
                        </Typography>
                        <Circle sx={{ fontSize: 8, color: ss.color }} />
                        <Typography
                          variant="caption"
                          sx={{ color: ss.color, fontWeight: 600 }}
                        >
                          {ss.label}
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {svc.image ||
                          (svc.buildConfig
                            ? "Built from Dockerfile"
                            : "No image")}
                        {svc.internalPort ? ` · Port ${svc.internalPort}` : ""}
                        {svc.containerId
                          ? ` · ${svc.containerId.substring(0, 12)}`
                          : ""}
                      </Typography>
                    </Box>

                    {/* Actions */}
                    <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                      {isStopped && !isDeploying && (
                        <Tooltip title="Deploy">
                          <IconButton
                            size="small"
                            onClick={() => svcDeploy(svc.id)}
                            disabled={deployLoading}
                            color="primary"
                          >
                            <PlayArrow fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {isRunning && (
                        <>
                          <Tooltip title="Restart">
                            <IconButton
                              size="small"
                              onClick={() => svcRestart(svc.id)}
                              disabled={deployLoading}
                            >
                              <RestartAlt fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Stop">
                            <IconButton
                              size="small"
                              onClick={() => svcStop(svc.id)}
                              disabled={deployLoading}
                              color="error"
                            >
                              <Stop fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {/* After stop, show Deploy button again (handled by isStopped above) */}
                      <Tooltip title="Logs">
                        <IconButton
                          size="small"
                          onClick={() => svcLogs(svc.id)}
                          disabled={!svc.containerId}
                        >
                          <Terminal fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => svcDelete(svc.id)}
                          disabled={deployLoading}
                          sx={{
                            color: "text.disabled",
                            "&:hover": { color: "error.main" },
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          )}
        </Box>
      )}

      {/* Tab: Environment */}
      {activeTab === 1 && (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Environment Variables
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Manage environment variables per service. Changes require a redeploy
            to take effect.
          </Typography>

          {services.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Add a service first to manage environment variables.
            </Typography>
          ) : (
            services.map((svc) => {
              const isEditing = envEditing === svc.id;
              const rawEnv = svc.envVars;
              const entries = Array.isArray(rawEnv)
                ? rawEnv.map((v) => [v.key, v.value])
                : Object.entries(rawEnv || {});
              return (
                <Box key={svc.id} sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={600}>
                      {svc.name}
                    </Typography>
                    {!isEditing ? (
                      <Button
                        size="small"
                        startIcon={<Edit sx={{ fontSize: 14 }} />}
                        onClick={() => startEnvEdit(svc)}
                        sx={{ textTransform: "none" }}
                      >
                        Edit
                      </Button>
                    ) : (
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          size="small"
                          onClick={cancelEnvEdit}
                          sx={{ textTransform: "none" }}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<Save sx={{ fontSize: 14 }} />}
                          onClick={saveEnvVars}
                          disabled={envSaving || !envDirty}
                          disableElevation
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: 1.5,
                          }}
                        >
                          {envSaving ? "Saving..." : "Save"}
                        </Button>
                      </Box>
                    )}
                  </Box>

                  {isEditing ? (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      {envRows.map((row, idx) => (
                        <Box
                          key={idx}
                          sx={{ display: "flex", gap: 1, alignItems: "center" }}
                        >
                          <TextField
                            size="small"
                            placeholder="KEY"
                            value={row.key}
                            onChange={(e) =>
                              updateEnvRow(idx, "key", e.target.value)
                            }
                            sx={{
                              flex: 1,
                              "& input": {
                                fontFamily: "monospace",
                                fontSize: "0.85rem",
                              },
                            }}
                          />
                          <Typography sx={{ color: "text.disabled" }}>
                            =
                          </Typography>
                          <TextField
                            size="small"
                            placeholder="value"
                            value={row.value}
                            onChange={(e) =>
                              updateEnvRow(idx, "value", e.target.value)
                            }
                            sx={{
                              flex: 2,
                              "& input": {
                                fontFamily: "monospace",
                                fontSize: "0.85rem",
                              },
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => removeEnvRow(idx)}
                            sx={{
                              color: "text.disabled",
                              "&:hover": { color: "error.main" },
                            }}
                          >
                            <RemoveCircleOutline fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                      <Button
                        size="small"
                        startIcon={<AddCircleOutline sx={{ fontSize: 16 }} />}
                        onClick={addEnvRow}
                        sx={{ textTransform: "none", alignSelf: "flex-start" }}
                      >
                        Add Variable
                      </Button>
                      {envDirty && (
                        <Alert
                          severity="info"
                          sx={{ mt: 1, borderRadius: 1.5 }}
                        >
                          After saving, redeploy the service for changes to take
                          effect.
                        </Alert>
                      )}
                    </Box>
                  ) : entries.length === 0 ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      No environment variables configured
                    </Typography>
                  ) : (
                    <Box
                      sx={{
                        borderRadius: 1.5,
                        border: "1px solid",
                        borderColor: "divider",
                        overflow: "hidden",
                      }}
                    >
                      {entries.map(([key, value], idx) => (
                        <Box
                          key={key}
                          sx={{
                            display: "flex",
                            gap: 2,
                            px: 2,
                            py: 1,
                            bgcolor:
                              idx % 2 === 0 ? "transparent" : "action.hover",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "monospace",
                              fontWeight: 600,
                              minWidth: 150,
                            }}
                          >
                            {key}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              fontFamily: "monospace",
                              flex: 1,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {/* {value} */}
                            {isSensitiveKey(key) ? "••••••••" : value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                  {/* Volumes Section */}
                  <Box sx={{ mt: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={600}>
                        Volumes
                      </Typography>
                      {volumeEditing !== svc.id ? (
                        <Button
                          size="small"
                          startIcon={<Edit sx={{ fontSize: 14 }} />}
                          onClick={() => startVolumeEdit(svc)}
                          sx={{ textTransform: "none" }}
                        >
                          Edit Volumes
                        </Button>
                      ) : (
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button
                            size="small"
                            onClick={() => setVolumeEditing(null)}
                            sx={{ textTransform: "none" }}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<Save sx={{ fontSize: 14 }} />}
                            onClick={() => saveVolumes(svc.id)}
                            disableElevation
                            sx={{
                              textTransform: "none",
                              fontWeight: 600,
                              borderRadius: 1.5,
                            }}
                          >
                            Save
                          </Button>
                        </Box>
                      )}
                    </Box>

                    {volumeEditing === svc.id ? (
                      // Editable volume rows
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.5,
                        }}
                      >
                        {volumeRows.map((row, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                              flexWrap: "wrap",
                            }}
                          >
                            <TextField
                              size="small"
                              placeholder="Host path / volume name"
                              value={row.source}
                              onChange={(e) => {
                                const newRows = [...volumeRows];
                                newRows[idx].source = e.target.value;
                                setVolumeRows(newRows);
                              }}
                              sx={{ flex: 2, minWidth: 150 }}
                              InputProps={{
                                sx: {
                                  fontFamily: "monospace",
                                  fontSize: "0.8rem",
                                },
                              }}
                            />
                            <Typography sx={{ color: "text.disabled" }}>
                              :
                            </Typography>
                            <TextField
                              size="small"
                              placeholder="Container path"
                              value={row.target}
                              onChange={(e) => {
                                const newRows = [...volumeRows];
                                newRows[idx].target = e.target.value;
                                setVolumeRows(newRows);
                              }}
                              sx={{ flex: 2, minWidth: 150 }}
                              InputProps={{
                                sx: {
                                  fontFamily: "monospace",
                                  fontSize: "0.8rem",
                                },
                              }}
                            />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={row.persistent || false}
                                  onChange={(e) => {
                                    const newRows = [...volumeRows];
                                    newRows[idx].persistent = e.target.checked;
                                    setVolumeRows(newRows);
                                  }}
                                />
                              }
                              label="Persist"
                              sx={{ mr: 1 }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => {
                                const newRows = volumeRows.filter(
                                  (_, i) => i !== idx,
                                );
                                setVolumeRows(newRows);
                              }}
                              sx={{
                                color: "text.disabled",
                                "&:hover": { color: "error.main" },
                              }}
                            >
                              <RemoveCircleOutline fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                        <Button
                          size="small"
                          startIcon={<AddCircleOutline sx={{ fontSize: 16 }} />}
                          onClick={() =>
                            setVolumeRows((prev) => [
                              ...prev,
                              {
                                source: "",
                                target: "",
                                mode: "rw",
                                persistent: false,
                              },
                            ])
                          }
                          sx={{
                            textTransform: "none",
                            alignSelf: "flex-start",
                          }}
                        >
                          Add Volume
                        </Button>
                      </Box>
                    ) : // Read‑only volume list
                    svc.volumes && svc.volumes.length > 0 ? (
                      <Box
                        sx={{
                          borderRadius: 1.5,
                          border: "1px solid",
                          borderColor: "divider",
                          overflow: "hidden",
                        }}
                      >
                        {svc.volumes.map((vol, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              display: "flex",
                              gap: 2,
                              px: 2,
                              py: 1,
                              bgcolor:
                                idx % 2 === 0 ? "transparent" : "action.hover",
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: "monospace",
                                fontWeight: 600,
                                minWidth: 180,
                              }}
                            >
                              {vol.source || vol.host}
                            </Typography>
                            <Typography sx={{ color: "text.disabled" }}>
                              →
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontFamily: "monospace", flex: 1 }}
                            >
                              {vol.target || vol.container}
                            </Typography>
                            {vol.persistent && (
                              <Chip
                                label="Persistent"
                                size="small"
                                color="success"
                                sx={{ height: 20, fontSize: "0.65rem" }}
                              />
                            )}
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ ml: 1 }}
                      >
                        No volumes configured
                      </Typography>
                    )}
                  </Box>
                  {(svc.serviceType === "DATABASE" ||
                    svc.serviceType === "CACHE") && (
                    <Alert severity="info" sx={{ mt: 2, borderRadius: 1.5 }}>
                      <Typography variant="body2" fontWeight={600}>
                        Internal connection — use in your app service
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace" }}
                      >
                        Host: <strong>{svc.name}</strong>
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace" }}
                      >
                        Port: <strong>{svc.internalPort}</strong>
                      </Typography>
                    </Alert>
                  )}
                </Box>
              );
            })
          )}

          {/* Git config section */}
          {app.deploymentType === "GIT_DOCKERFILE" && gitConfig && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Git Configuration
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <InfoRow
                  label="Repository"
                  value={gitConfig.repoUrl}
                  mono
                  copy={copy}
                />
                <InfoRow label="Branch" value={gitConfig.branch || "main"} />
                <InfoRow
                  label="Dockerfile"
                  value={gitConfig.dockerfilePath || "Dockerfile"}
                />
              </Box>
            </>
          )}
        </Paper>
      )}

      {/* Tab: Logs */}
      {activeTab === 2 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Mode selector */}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Chip
              label="Container Logs"
              variant={logsMode === "container" ? "filled" : "outlined"}
              color={logsMode === "container" ? "primary" : "default"}
              onClick={() => setLogsMode("container")}
              sx={{ fontWeight: 600 }}
            />
            <Chip
              label="Deployment Activity"
              variant={logsMode === "deployment" ? "filled" : "outlined"}
              color={logsMode === "deployment" ? "primary" : "default"}
              onClick={() => setLogsMode("deployment")}
              sx={{ fontWeight: 600 }}
            />
          </Box>

          {/* Container Logs */}
          {logsMode === "container" && (
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
              {/* Controls */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  Container Logs
                </Typography>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel>Tail</InputLabel>
                    <Select
                      value={logsTail}
                      label="Tail"
                      onChange={(e) => setLogsTail(e.target.value)}
                    >
                      <MenuItem value={50}>50 lines</MenuItem>
                      <MenuItem value={100}>100 lines</MenuItem>
                      <MenuItem value={500}>500 lines</MenuItem>
                      <MenuItem value={1000}>1000 lines</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    variant={logsAutoRefresh ? "contained" : "outlined"}
                    size="small"
                    onClick={toggleAutoRefresh}
                    startIcon={logsAutoRefresh ? <Stop /> : <PlayArrow />}
                    sx={{ textTransform: "none" }}
                  >
                    {logsAutoRefresh ? "Stop" : "Follow"}
                  </Button>
                </Box>
              </Box>

              {/* No services */}
              {services.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No services available.
                </Typography>
              ) : (
                <>
                  {/* Service tabs */}
                  <Tabs
                    value={logsActiveService || services[0]?.id || false}
                    onChange={(_, newValue) => {
                      setLogsActiveService(newValue);
                      fetchLogs(newValue);
                    }}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ mb: 2 }}
                  >
                    {services.map((svc) => (
                      <Tab
                        key={svc.id}
                        label={svc.name}
                        value={svc.id}
                        icon={
                          <Circle
                            sx={{
                              fontSize: 8,
                              color:
                                serviceStatusStyles[svc.status]?.color ||
                                "#6b7280",
                            }}
                          />
                        }
                        iconPosition="start"
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          minHeight: 40,
                        }}
                      />
                    ))}
                  </Tabs>

                  {/* Logs area */}
                  {logsActiveService ? (
                    renderLogsContent(logsActiveService)
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Select a service to view logs.
                    </Typography>
                  )}
                </>
              )}
            </Paper>
          )}

          {/* Deployment Activity */}
          {logsMode === "deployment" && (
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Deployment Activity
              </Typography>
              {(() => {
                const allLogs = (app.services || [])
                  .flatMap((s) =>
                    (s.deploymentLogs || []).map((l) => ({
                      ...l,
                      serviceName: l.serviceName || s.name,
                    })),
                  )
                  .sort(
                    (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
                  );
                return allLogs.length > 0 ? (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                  >
                    {allLogs.map((log, i) => (
                      <Box
                        key={log.id || i}
                        sx={{
                          p: 2,
                          borderRadius: 1.5,
                          border: "1px solid",
                          borderColor: "divider",
                          borderLeft: 3,
                          borderLeftColor:
                            log.status === "SUCCESS"
                              ? "success.main"
                              : log.status === "FAILED"
                                ? "error.main"
                                : "warning.main",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 0.5,
                          }}
                        >
                          <Typography variant="subtitle2">
                            {log.action}{" "}
                            {log.serviceName ? `- ${log.serviceName}` : ""}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(log.createdAt).toLocaleString()}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          label={log.status}
                          color={
                            log.status === "SUCCESS"
                              ? "success"
                              : log.status === "FAILED"
                                ? "error"
                                : "warning"
                          }
                          sx={{ height: 20, fontSize: "0.7rem" }}
                        />
                        {log.message && (
                          <Typography
                            variant="body2"
                            sx={{
                              mt: 1,
                              fontFamily: "monospace",
                              fontSize: "0.8rem",
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {log.message}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No deployment activity yet. Deploy your application to see
                    logs here.
                  </Typography>
                );
              })()}
            </Paper>
          )}
        </Box>
      )}

      {/* Tab: APM */}
      {activeTab === 3 && (
        <ApmTab
          targetType="application"
          targetId={id}
          targetName={app?.name || "Application"}
        />
      )}

      {/* Tab: Settings */}
      {activeTab === 4 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Custom Domain */}
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
            <AppCustomDomainSection
              application={app}
              appId={id}
              onRefresh={refresh}
            />
          </Paper>

          {/* App Info */}
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Application Info
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <InfoRow label="ID" value={app.id} mono copy={copy} />
              <InfoRow
                label="Network"
                value={app.networkName}
                mono
                copy={copy}
              />
              <InfoRow
                label="Created"
                value={new Date(app.createdAt).toLocaleString()}
              />
              <InfoRow
                label="Updated"
                value={new Date(app.updatedAt).toLocaleString()}
              />
              {app.workerId && (
                <InfoRow
                  label="Worker"
                  value={app.worker?.name || app.workerId}
                />
              )}
            </Box>
          </Paper>

          {/* Git / Build Configuration - per service */}
          {app.services
            ?.filter((s) => s.buildConfig?.buildType === "GIT_DOCKERFILE")
            .map((svc) => (
              <Paper
                key={svc.id}
                variant="outlined"
                sx={{ p: 3, borderRadius: 2 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={600}>
                    Git Configuration — {svc.name}
                  </Typography>
                  {gitConfigEditing !== svc.id && (
                    <Button
                      size="small"
                      startIcon={<Edit />}
                      onClick={() => handleGitConfigEdit(svc)}
                      sx={{ textTransform: "none" }}
                    >
                      Edit
                    </Button>
                  )}
                </Box>
                {gitConfigEditing === svc.id ? (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      label="Repository URL"
                      value={gitConfigForm.repoUrl}
                      onChange={(e) =>
                        setGitConfigForm((f) => ({
                          ...f,
                          repoUrl: e.target.value,
                        }))
                      }
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Branch"
                      value={gitConfigForm.branch}
                      onChange={(e) =>
                        setGitConfigForm((f) => ({
                          ...f,
                          branch: e.target.value,
                        }))
                      }
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Dockerfile Path"
                      value={gitConfigForm.dockerfilePath}
                      onChange={(e) =>
                        setGitConfigForm((f) => ({
                          ...f,
                          dockerfilePath: e.target.value,
                        }))
                      }
                      helperText="Relative to build context (e.g., Dockerfile)"
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Build Context"
                      value={gitConfigForm.buildContext}
                      onChange={(e) =>
                        setGitConfigForm((f) => ({
                          ...f,
                          buildContext: e.target.value,
                        }))
                      }
                      helperText="Directory relative to repo root (e.g., . or server)"
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Access Token"
                      type="password"
                      value={gitConfigForm.accessToken}
                      onChange={(e) =>
                        setGitConfigForm((f) => ({
                          ...f,
                          accessToken: e.target.value,
                        }))
                      }
                      helperText="GitHub personal access token for private repos (leave empty if public)"
                    />
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        justifyContent: "flex-end",
                      }}
                    >
                      <Button
                        size="small"
                        onClick={() => setGitConfigEditing(null)}
                        sx={{ textTransform: "none" }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<Save />}
                        onClick={handleGitConfigSave}
                        disabled={gitConfigSaving}
                        sx={{ textTransform: "none" }}
                      >
                        {gitConfigSaving ? "Saving..." : "Save"}
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <InfoRow
                      label="Repo URL"
                      value={svc.buildConfig?.repoUrl}
                      mono
                      copy={copy}
                    />
                    <InfoRow label="Branch" value={svc.buildConfig?.branch} />
                    <InfoRow
                      label="Dockerfile"
                      value={svc.buildConfig?.dockerfilePath}
                    />
                    <InfoRow
                      label="Context"
                      value={svc.buildConfig?.buildContext || "."}
                    />
                    <InfoRow
                      label="Auth"
                      value={
                        svc.buildConfig?.credentials
                          ? "Token configured"
                          : "None (public)"
                      }
                    />
                  </Box>
                )}
              </Paper>
            ))}

          {/* WordPress */}
          {app.deploymentType === "WORDPRESS" && app.site && (
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                WordPress
              </Typography>
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNew />}
                  onClick={() =>
                    window.open(
                      `${protocol}://${app.site.customDomain || app.site.domain}`,
                      "_blank",
                    )
                  }
                  sx={{ textTransform: "none" }}
                >
                  Visit Site
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNew />}
                  onClick={() =>
                    window.open(
                      `${protocol}://${app.site.customDomain || app.site.domain}/wp-admin`,
                      "_blank",
                    )
                  }
                  sx={{ textTransform: "none" }}
                >
                  WP Admin
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate(`/dashboard/sites/${app.site.id}`)}
                  sx={{ textTransform: "none" }}
                >
                  Full Site Management
                </Button>
              </Box>
            </Paper>
          )}

          {/* Danger Zone */}
          <Paper
            variant="outlined"
            sx={{ p: 3, borderRadius: 2, borderColor: "error.light" }}
          >
            <Typography
              variant="subtitle1"
              fontWeight={600}
              color="error"
              gutterBottom
            >
              Danger Zone
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Permanently delete this application and all its services. This
              cannot be undone.
            </Typography>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<Delete />}
              onClick={handleDelete}
              sx={{ textTransform: "none" }}
            >
              Delete Application
            </Button>
          </Paper>
        </Box>
      )}

      {/* Edit Dialog */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Edit Application</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Name"
            value={editForm.name}
            onChange={(e) =>
              setEditForm((p) => ({ ...p, name: e.target.value }))
            }
            sx={{ mt: 2, mb: 2 }}
            size="small"
          />
          <TextField
            fullWidth
            label="Description"
            value={editForm.description}
            onChange={(e) =>
              setEditForm((p) => ({ ...p, description: e.target.value }))
            }
            multiline
            rows={3}
            size="small"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setEditOpen(false)}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEditSave}
            disableElevation
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 1.5 }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Service Dialog */}
      <AddServiceDialog
        open={addServiceOpen}
        onClose={() => setAddServiceOpen(false)}
        applicationId={id}
        onSuccess={refresh}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={closeConfirm}
        onConfirm={confirmDialog.onConfirm || closeConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        confirmColor={confirmDialog.confirmColor}
      />
    </Box>
  );
};

// Helper component for key-value info rows
const InfoRow = ({ label, value, mono, copy }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ minWidth: 100, flexShrink: 0 }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontFamily: mono ? "monospace" : "inherit",
        fontSize: mono ? "0.8rem" : "inherit",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        flex: 1,
      }}
    >
      {value || "—"}
    </Typography>
    {copy && value && (
      <IconButton
        size="small"
        onClick={() => copy(value)}
        sx={{ flexShrink: 0 }}
      >
        <ContentCopy sx={{ fontSize: 14 }} />
      </IconButton>
    )}
  </Box>
);

export default ApplicationDetail;
